import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Users, Image } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImageUploader from '../components/ImageUploader';
import { useImageUpload } from '../hooks/useImageUpload';

const empty = { batchName:'', type:'language', tradeType:'', duration:'', startDate:'', endDate:'', status:'upcoming', totalSeats:'', enrolledCount:'0', description:'', imageUrl:'', imagePublicId:'' };
const statusMap   = { upcoming:'Upcoming', ongoing:'Ongoing', completed:'Completed' };
const typeMap     = { language:'Language', trade:'Trade', both:'Language + Trade' };
const statusColor = { upcoming:'bg-blue-100 text-blue-700', ongoing:'bg-green-100 text-green-700', completed:'bg-slate-100 text-slate-600' };

export default function Batches() {
  const [batches, setBatches]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState(null);
  const [form, setForm]             = useState(empty);
  const [editing, setEditing]       = useState(false);
  const { upload, uploading, preview, setPreview } = useImageUpload('/upload/batch-image');

  const fetchBatches = async () => { setLoading(true); const {data}=await API.get('/batches'); setBatches(data); setLoading(false); };
  useEffect(()=>{ fetchBatches(); },[]);

  const openCreate = () => { setForm(empty); setPreview(''); setEditing(false); setModalOpen(true); };
  const openEdit   = (b) => { setForm({...b, startDate:b.startDate?.split('T')[0]||'', endDate:b.endDate?.split('T')[0]||''}); setPreview(b.imageUrl||''); setEditing(true); setModalOpen(true); };

  const handleImageUpload = async (file) => { const r=await upload(file); if(r) setForm(f=>({...f,imageUrl:r.imageUrl,imagePublicId:r.publicId})); };
  const handleImageRemove = () => { setPreview(''); setForm(f=>({...f,imageUrl:'',imagePublicId:''})); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await API.put(`/batches/${form._id}`, form); toast.success('Batch updated successfully!'); }
      else         { await API.post('/batches', form);            toast.success('Batch created successfully!'); }
      setModalOpen(false); fetchBatches();
    } catch(err) { toast.error(err.response?.data?.message||'Something went wrong!'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/batches/${deleteId}`); toast.success('Batch deleted!'); setConfirmOpen(false); fetchBatches(); }
    catch { toast.error('Could not delete batch!'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><Users className="text-blue-600" size={22}/></div>
          <div><h1 className="text-xl font-bold text-slate-800">Batch Management</h1><p className="text-slate-500 text-sm">Manage all batches</p></div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={18}/> New Batch
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-4 border-red-700 border-t-transparent"/></div> : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>{['Batch Name','Type','Duration','Seats','Status','Image','Actions'].map(h=><th key={h} className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {batches.length===0
                ? <tr><td colSpan="7" className="text-center py-10 text-slate-400">No batches found. Create one!</td></tr>
                : batches.map(b=>(
                  <tr key={b._id} className="hover:bg-slate-50">
                    <td className="py-3 px-4"><p className="font-medium text-slate-800 text-sm">{b.batchName}</p><p className="text-xs text-slate-400">{b.tradeType}</p></td>
                    <td className="py-3 px-4 text-sm text-slate-600">{typeMap[b.type]}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{b.duration}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{b.enrolledCount}/{b.totalSeats}</td>
                    <td className="py-3 px-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor[b.status]}`}>{statusMap[b.status]}</span></td>
                    <td className="py-3 px-4">
                      {b.imageUrl
                        ? <img src={b.imageUrl} alt="" className="rounded border border-slate-200" style={{width:64,height:36,objectFit:"cover",objectPosition:"center"}}/>
                        : <div className="w-12 h-8 bg-slate-100 rounded flex items-center justify-center"><Image size={14} className="text-slate-400"/></div>}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button onClick={()=>openEdit(b)} className="bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs transition-colors"><Pencil size={13}/></button>
                        <button onClick={()=>{setDeleteId(b._id);setConfirmOpen(true);}} className="bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg text-xs transition-colors"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editing?'Edit Batch':'Create New Batch'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Batch Name *</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.batchName} onChange={e=>setForm({...form,batchName:e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                <option value="language">Language</option><option value="trade">Trade</option><option value="both">Language + Trade</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Trade Type</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="e.g. Welding" value={form.tradeType} onChange={e=>setForm({...form,tradeType:e.target.value})}/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Duration *</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="e.g. 3 months" required value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})}/>
            </div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                <option value="upcoming">Upcoming</option><option value="ongoing">Ongoing</option><option value="completed">Completed</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
              <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.startDate} onChange={e=>setForm({...form,startDate:e.target.value})}/>
            </div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
              <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.endDate} onChange={e=>setForm({...form,endDate:e.target.value})}/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Total Seats *</label>
              <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.totalSeats} onChange={e=>setForm({...form,totalSeats:e.target.value})}/>
            </div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Enrolled Count</label>
              <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" value={form.enrolledCount} onChange={e=>setForm({...form,enrolledCount:e.target.value})}/>
            </div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/>
          </div>
          <ImageUploader label="Batch Image (optional)" hint="1280 × 720 px recommended (16:9 ratio)" currentUrl={preview} onUpload={handleImageUpload} onRemove={handleImageRemove} uploading={uploading}/>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={()=>setModalOpen(false)} className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium text-sm">Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog isOpen={confirmOpen} onConfirm={handleDelete} onCancel={()=>setConfirmOpen(false)} message="Deleting this batch will also remove its image. Are you sure?"/>
    </div>
  );
}
